// src/miniapps/lex-youtube-to-media/lex-youtube-to-media.js
// PURE FIRESTORE VERSION (defensive + DEBUG)
// - “All” 只是全库（永远不删里面的 track），从 All “加入到别的歌单” = 复制，不移动
// - 删除歌单使用 deletePlaylistKeepTracks：先把曲目搬去 All，再删歌单
// - 轨道的重命名 / 删除 / 拖拽重排 / 跨歌单移动 全部走 Firestore
// - 严格防呆：uid / playlistId / allPlaylistId 未就绪时不去触发任何 firestore 读写
// - Share 卡片整段注释掉
// - 全面增加 DEBUG 日志，定位 undefined path
// ----------------------------------------------------------------------------

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../user/AuthContext';
import LexLoginSignupPopup from '../lex-loginsignup-popup/lex-loginsignup-popup';

import {
  listenPlaylists,
  listenTracks,
  addPlaylist as fbAddPlaylist,
  addTrack as fbAddTrack,
  ensureAllPlaylist,
  deletePlaylistKeepTracks,
  updateTrack as fbUpdateTrack,
  moveTrack as fbMoveTrack,
  reorderTracks as fbReorderTracks,
  deleteTrack as fbDeleteTrack,
} from '../../firebase';

import './lex-youtube-to-media.css';
const API = process.env.REACT_APP_API_URL || 'https://api.lextrackai.com';


const ALL = 'all';

// ───────────────────────── DEBUG Helpers ─────────────────────────
const DEBUG = true;
function d(...args) {
  if (DEBUG) console.debug('[LexYTPlayer]', ...args);
}
function w(...args) {
  if (DEBUG) console.warn('[LexYTPlayer]', ...args);
}
function e(...args) {
  console.error('[LexYTPlayer]', ...args);
}
// 快速判断 uid / pid 等是否就绪
function assertReady(label, obj) {
  const keys = Object.keys(obj);
  let ok = true;
  const meta = {};
  keys.forEach(k => {
    meta[k] = obj[k];
    if (obj[k] === null || obj[k] === undefined || obj[k] === '') ok = false;
  });
  if (!ok) {
    w(label + ' – required not ready:', meta);
  } else {
    d(label + ' – OK:', meta);
  }
  return ok;
}

// ───────────────────────── Component ─────────────────────────
export default function LexYTPlayer() {
  const { user } = useAuth();

  /* ───────── auth gate ───────── */
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);
  const requireAuth = useCallback(
    fn => {
      if (!user) setLoginPromptVisible(true);
      else fn && fn();
    },
    [user]
  );

  /* ───────── state ───────── */
  const [playlists, setPlaylists] = useState([]); // {id, name, ...}
  const [allPlaylistId, setAllPlaylistId] = useState(null);

  const [filterId, setFilterId] = useState(ALL); // 'all' | playlistId
  const [queue, setQueue] = useState([]); // 当前 filter 下订阅的曲目

  const [loading, setLoading] = useState(false); // converting flag
  const [currentUrl, setCurrentUrl] = useState('');

  const [currentId, setCurrentId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playProgress, setPlayProg] = useState(0);
  const [playSec, setPlaySec] = useState(0);

  const [volume, setVolume] = useState(1);
  const [shuffle, setShuffle] = useState(false);
  const [repeatOne, setRepeatOne] = useState(false);

  const [timerMsLeft, setTimerMsLeft] = useState(null);
  const [showTimerCfg, setShowTimerCfg] = useState(false);
  const timerRef = useRef(null);

  // Share（已禁用）
  // const [showShare, setShowShare] = useState(false);
  // const [copied, setCopied] = useState(false);

  const [addingPl, setAddingPl] = useState(false);
  const [newPlName, setNewPlName] = useState('');

  const [openPlFor, setOpenPlFor] = useState(null); // which track row shows playlist picker

  const audioRef = useRef(null);

  /* ───────── derived ───────── */
  const currentItem = queue.find(t => t.id === currentId) || null;
  const visibleQueue = queue;

  /* ───────── submit / convert (Firestore only) ───────── */
  const submit = async () => {
    d('submit() called');
    if (!currentUrl.trim()) return;
    if (!user || !user.uid) {
      w('submit blocked: user/uid not ready', { user, uid: user && user.uid });
      return setLoginPromptVisible(true);
    }

    const title = extractTitleGuess(currentUrl.trim());
    setLoading(true);

    try {
      let pid;
      if (filterId === ALL) {
        d('submit in ALL view', { allPlaylistId });
        // 需要确保 allPlaylistId 已存在
        if (!allPlaylistId) {
          d('allPlaylistId missing; calling ensureAllPlaylist', { uid: user.uid });
          pid = await ensureAllPlaylist(user.uid);
          setAllPlaylistId(pid);
        } else {
          pid = allPlaylistId;
        }
      } else {
        pid = filterId;
      }

      if (!assertReady('submit pid ready?', { pid })) {
        w('submit: playlistId not ready, skip');
        return;
      }

      d('processAudio', { url: currentUrl.trim() });
      const data = await processAudio({ url: currentUrl.trim() });

      d('fbAddTrack', { uid: user.uid, pid });
      const newId = await fbAddTrack(user.uid, pid, {
        title,
        mp3Url: data.mp3Url,
        duration: secondsToMinStr(data.durationSec || 0),
      });

      d('fbAddTrack done, newId=', newId);

      // autoplay the newly added (after listener updates queue)
      setTimeout(() => {
        playItem(newId, true);
      }, 600);
    } catch (err) {
      e('submit error', err);
    } finally {
      setLoading(false);
      setCurrentUrl('');
    }
  };

  /* ───────── player control ───────── */
  const playItem = useCallback(
    (id, force = false) => {
      const item = queue.find(v => v.id === id);
      if (!item || !item.mp3Url) return;
      setCurrentId(id);
      if (audioRef.current) {
        if (!force && audioRef.current.src === item.mp3Url && !audioRef.current.paused) return;
        audioRef.current.src = item.mp3Url;
        audioRef.current.play().catch(() => {});
      }
    },
    [queue]
  );

  const togglePlay = () => {
    if (!audioRef.current) return;
    audioRef.current.paused
      ? audioRef.current.play().catch(() => {})
      : audioRef.current.pause();
  };

  const seekTo = ratio => {
    if (!audioRef.current || isNaN(audioRef.current.duration)) return;
    audioRef.current.currentTime = ratio * audioRef.current.duration;
  };

  /* ───────── audio events ───────── */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => {
      if (!audio.duration) return;
      setPlayProg(audio.currentTime / audio.duration);
      setPlaySec(audio.currentTime);
    };
    const onEnded = () => {
      if (repeatOne) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
        return;
      }
      const list = visibleQueue;
      if (shuffle && list.length > 1) {
        let next;
        do {
          next = list[Math.floor(Math.random() * list.length)];
        } while (next.id === currentId);
        playItem(next.id, true);
        return;
      }
      const idx = list.findIndex(i => i.id === currentId);
      if (idx >= 0 && idx < list.length - 1) playItem(list[idx + 1].id, true);
      else setIsPlaying(false);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnded);
    };
  }, [queue, currentId, playItem, visibleQueue, shuffle, repeatOne]);

  /* ───────── volume ───────── */
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  /* ───────── sleep timer ───────── */
  useEffect(() => {
    if (timerMsLeft == null) return;
    clearInterval(timerRef.current);
    const end = Date.now() + timerMsLeft;
    timerRef.current = setInterval(() => {
      const left = end - Date.now();
      if (left <= 0) {
        clearInterval(timerRef.current);
        setTimerMsLeft(null);
        audioRef.current?.pause();
      } else {
        setTimerMsLeft(left);
      }
    }, 500);
    return () => clearInterval(timerRef.current);
  }, [timerMsLeft]);

  const startTimer = mins => {
    if (!mins || mins <= 0) return;
    setTimerMsLeft(mins * 60 * 1000);
    setShowTimerCfg(false);
  };
  const cancelTimer = () => {
    clearInterval(timerRef.current);
    setTimerMsLeft(null);
    setShowTimerCfg(false);
  };

  /* ───────── playlist actions ───────── */
  const onDeletePlaylist = useCallback(
    async id => {
      d('onDeletePlaylist', { id, uid: user && user.uid, allPlaylistId });
      if (!user || !user.uid || !id) {
        w('onDeletePlaylist blocked: missing uid/id', { user, id });
        return;
      }
      try {
        await deletePlaylistKeepTracks(user.uid, id, allPlaylistId || undefined);
        if (filterId === id) setFilterId(ALL);
      } catch (err) {
        e('onDeletePlaylist error', err);
      }
    },
    [user, filterId, allPlaylistId]
  );

  const onMoveTrack = useCallback(
    async (trackId, toPlaylistId) => {
      d('onMoveTrack', {
        trackId,
        toPlaylistId,
        filterId,
        allPlaylistId,
        uid: user && user.uid,
      });

      if (!user || !user.uid || !trackId || !toPlaylistId) {
        w('onMoveTrack blocked: missing uid/trackId/toPlaylistId', {
          user,
          trackId,
          toPlaylistId,
        });
        return;
      }
      const fromPid = filterId === ALL ? allPlaylistId : filterId;
      if (!fromPid) {
        w('onMoveTrack blocked: fromPid not ready', { fromPid, filterId, allPlaylistId });
        return;
      }

      try {
        // 从 All -> 目标歌单：复制（不删 All）
        if (fromPid === allPlaylistId) {
          const t = queue.find(t => t.id === trackId);
          if (!t) return;
          await fbAddTrack(user.uid, toPlaylistId, {
            title: t.title,
            mp3Url: t.mp3Url,
            duration: t.duration,
          });
        } else {
          await fbMoveTrack(user.uid, fromPid, toPlaylistId, trackId);
        }
      } catch (err) {
        e('onMoveTrack error', err);
      }
    },
    [user, filterId, allPlaylistId, queue]
  );

  const onRenameTrack = useCallback(
    async (trackId, title) => {
      d('onRenameTrack', {
        trackId,
        title,
        filterId,
        allPlaylistId,
        uid: user && user.uid,
      });
      if (!user || !user.uid || !trackId) {
        w('onRenameTrack blocked: missing uid/trackId', { user, trackId });
        return;
      }
      const pid = filterId === ALL ? allPlaylistId : filterId;
      if (!pid) {
        w('onRenameTrack blocked: pid not ready', { pid, filterId, allPlaylistId });
        return;
      }
      try {
        await fbUpdateTrack(user.uid, pid, trackId, { title });
      } catch (err) {
        e('onRenameTrack error', err);
      }
    },
    [user, filterId, allPlaylistId]
  );

  const onDeleteTrack = useCallback(
    async trackId => {
      d('onDeleteTrack', {
        trackId,
        filterId,
        allPlaylistId,
        uid: user && user.uid,
      });
      if (!user || !user.uid || !trackId) {
        w('onDeleteTrack blocked: missing uid/trackId', { user, trackId });
        return;
      }
      const pid = filterId === ALL ? allPlaylistId : filterId;
      if (!pid) {
        w('onDeleteTrack blocked: pid not ready', { pid, filterId, allPlaylistId });
        return;
      }
      try {
        await fbDeleteTrack(user.uid, pid, trackId);
        if (trackId === currentId) setCurrentId(null);
      } catch (err) {
        e('onDeleteTrack error', err);
      }
    },
    [user, filterId, allPlaylistId, currentId]
  );

  const onReorder = useCallback(
    async (dragId, dropId) => {
      d('onReorder', {
        dragId,
        dropId,
        filterId,
        allPlaylistId,
        uid: user && user.uid,
      });
      const list = [...queue];
      const fromIdx = list.findIndex(t => t.id === dragId);
      const toIdx = list.findIndex(t => t.id === dropId);
      if (fromIdx === -1 || toIdx === -1) return;

      const [it] = list.splice(fromIdx, 1);
      list.splice(toIdx, 0, it);
      setQueue(list);

      if (!user || !user.uid) {
        w('onReorder blocked: uid not ready', { user });
        return;
      }
      const pid = filterId === ALL ? allPlaylistId : filterId;
      if (!pid) {
        w('onReorder blocked: pid not ready', { pid });
        return;
      }
      try {
        await fbReorderTracks(
          user.uid,
          pid,
          list.map(t => t.id)
        );
      } catch (err) {
        e('onReorder error', err);
      }
    },
    [queue, user, filterId, allPlaylistId]
  );

  const createPlaylistAndAssign = useCallback(
    async (name, trackId) => {
      d('createPlaylistAndAssign', {
        name,
        trackId,
        uid: user && user.uid,
      });
      if (!name.trim() || !user || !user.uid) {
        w('createPlaylistAndAssign blocked', { name, user });
        return;
      }
      try {
        const newId = await fbAddPlaylist(user.uid, { name: name.trim() });
        await onMoveTrack(trackId, newId);
        setOpenPlFor(null);
      } catch (err) {
        e('createPlaylistAndAssign error', err);
      }
    },
    [user, onMoveTrack]
  );

  const addPlaylist = useCallback(
    async name => {
      d('addPlaylist', {
        name,
        uid: user && user.uid,
      });
      if (!name.trim() || !user || !user.uid) {
        w('addPlaylist blocked', { name, user });
        return;
      }
      try {
        await fbAddPlaylist(user.uid, { name: name.trim() });
        setAddingPl(false);
        setNewPlName('');
      } catch (err) {
        e('addPlaylist error', err);
      }
    },
    [user]
  );

  /* ───────── download mp3 ───────── */
  const downloadCurrentMp3 = useCallback(async () => {
    const item = currentItem;
    if (!item || !item.mp3Url) return;
    await downloadBlob(item.mp3Url, sanitizeName(item.title) + '.mp3');
  }, [currentItem]);

  /* ───────── global click to close popups ───────── */
  useEffect(() => {
    const close = e => {
      if (
        e.target.closest('.pl-picker') ||
        e.target.closest('.lytm-dropdown') ||
        e.target.closest('.icon-btn')
      )
        return;

      setShowTimerCfg(false);
      // setShowShare(false);
      setAddingPl(false);
      setOpenPlFor(null);
    };
    window.addEventListener('click', close, false);
    return () => window.removeEventListener('click', close, false);
  }, []);

  /* ───────── subscribe playlists ───────── */
  useEffect(() => {
    if (!user || !user.uid) {
      d('listenPlaylists: user not ready, cleaning states', { user });
      setPlaylists([]);
      setQueue([]);
      setAllPlaylistId(null);
      return;
    }
    d('listenPlaylists: start', { uid: user.uid });
    const unsub = listenPlaylists(user.uid, async pls => {
      d('listenPlaylists: snapshot', pls);
      setPlaylists(pls);
      const all = pls.find(p => (p.name || '').toLowerCase() === 'all');
      if (all) {
        d('listenPlaylists: found All', { id: all.id });
        setAllPlaylistId(all.id);
      } else {
        d('listenPlaylists: no All, ensure one...', { uid: user.uid });
        try {
          const id = await ensureAllPlaylist(user.uid);
          d('listenPlaylists: ensureAllPlaylist ok', { id });
          setAllPlaylistId(id);
        } catch (err) {
          e('ensureAllPlaylist error inside listenPlaylists', err);
        }
      }
    });
    return () => {
      d('listenPlaylists: unsub');
      unsub && unsub();
    };
  }, [user]);

  /* ───────── subscribe tracks of current filter ───────── */
  useEffect(() => {
    if (!user || !user.uid) {
      d('listenTracks blocked: user not ready', { user });
      return;
    }
    const pid = filterId === ALL ? allPlaylistId : filterId;
    if (!pid) {
      d('listenTracks: pid not ready, clean queue', { pid, filterId, allPlaylistId });
      setQueue([]);
      return;
    }

    d('listenTracks: start', { uid: user.uid, pid, filterId, allPlaylistId });
    const unsub = listenTracks(user.uid, pid, tracks => {
      d('listenTracks: snapshot', { pid, count: tracks.length });
      setQueue(
        tracks.map(t => ({
          id: t.id,
          title: t.title,
          mp3Url: t.mp3Url,
          duration: t.duration,
        }))
      );
    });
    return () => {
      d('listenTracks: unsub', { pid });
      unsub && unsub();
    };
  }, [user, filterId, allPlaylistId]);

  /* ───────── render ───────── */
  return (
    <>
      <LexLoginSignupPopup
        loginPromptVisible={loginPromptVisible}
        setLoginPromptVisible={setLoginPromptVisible}
      />

      <div className="lytm-root">
        {/* Sidebar – current queue */}
        <aside className="lytm-sidebar">
          <h2 className="lytm-sidebar-title">Tracks</h2>
          <ul className="lytm-list">
            {visibleQueue.map((v, i) => (
              <PlaylistRow
                key={v.id}
                item={v}
                index={i}
                active={currentId === v.id}
                playlists={playlists.filter(p => (p.name || '').toLowerCase() !== 'all')}
                onPlay={id => requireAuth(() => playItem(id, true))}
                onRename={onRenameTrack}
                onDelete={onDeleteTrack}
                onMove={onMoveTrack}
                onCreateAndMove={createPlaylistAndAssign}
                /* drag */
                onDragStart={(e, id) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', id);
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={(e, dropId) => {
                  const dragId = e.dataTransfer.getData('text/plain');
                  onReorder(dragId, dropId);
                }}
                onDragEnd={() => {}}
                /* playlist picker state */
                openPlFor={openPlFor}
                setOpenPlFor={setOpenPlFor}
              />
            ))}
          </ul>
        </aside>

        {/* Main */}
        <main className="lytm-main">
          {/* URL input */}
          <div className="lytm-row">
            <label className="lytm-label" htmlFor="yt-url"></label>
            <div className="lytm-urlbox">
              <input
                id="yt-url"
                className="lytm-input"
                placeholder="Enter YouTube URL here..."
                value={currentUrl}
                onChange={e => setCurrentUrl(e.target.value)}
                onKeyDown={e => (e.key === 'Enter' ? submit() : null)}
              />
              <button className="lytm-submit" onClick={submit} disabled={loading || !user}>
                {loading ? 'Converting…' : 'Convert'}
              </button>
            </div>
          </div>

          {/* Action Cards */}
          <div className="lytm-actions-grid">
            {/* Timer */}
            <ActionCard
              label={timerMsLeft ? `⏲ ${formatMs(timerMsLeft)}` : 'Timer ▾'}
              active={showTimerCfg}
              onClick={e => {
                e.stopPropagation();
                setShowTimerCfg(s => !s);
              }}
            >
              {showTimerCfg && (
                <Dropdown>
                  <input
                    type="number"
                    min="1"
                    placeholder="Minutes"
                    onKeyDown={e => e.key === 'Enter' && startTimer(Number(e.target.value))}
                  />
                  <button onClick={e => startTimer(Number(e.target.previousSibling.value))}>
                    Start
                  </button>
                  {timerMsLeft && <button onClick={cancelTimer}>Cancel</button>}
                </Dropdown>
              )}
            </ActionCard>

            {/* All filter */}
            <ActionCard
              label="All"
              active={filterId === ALL}
              onClick={e => {
                e.stopPropagation();
                setFilterId(ALL);
              }}
            />

            {/* dynamic playlists (without All) */}
            {playlists
              .filter(pl => (pl.name || '').toLowerCase() !== 'all')
              .map(pl => (
                <div key={pl.id} className="playlist-card-wrapper">
                  <ActionCard
                    label={pl.name}
                    active={filterId === pl.id}
                    onClick={e => {
                      e.stopPropagation();
                      setFilterId(pl.id);
                    }}
                  />
                  <button
                    className="icon-btn"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 16,
                      height: 16,
                      fontSize: 12,
                      lineHeight: '16px',
                    }}
                    title="删除歌单（曲目会移到 All）"
                    onClick={e => {
                      e.stopPropagation();
                      requireAuth(() => onDeletePlaylist(pl.id));
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}

            {/* Share （整段注释掉） */}
            {/*
            <ActionCard
              label="Share ▾"
              active={showShare}
              onClick={e => {
                e.stopPropagation();
                setShowShare(s => !s);
              }}
            >
              {showShare && (
                <Dropdown>
                  <input readOnly value={shareUrl} onClick={e => e.target.select()} />
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(shareUrl);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                  >
                    {copied ? 'Copied ✓' : 'Copy'}
                  </button>
                </Dropdown>
              )}
            </ActionCard>
            */}

            {/* Download current (mp3) */}
            <ActionCard
              label="Download current"
              onClick={e => {
                e.stopPropagation();
                requireAuth(downloadCurrentMp3);
              }}
            />

            {/* New Playlist */}
            <ActionCard
              label={addingPl ? 'Create ▸' : 'New Playlist ▾'}
              active={addingPl}
              onClick={e => {
                e.stopPropagation();
                setAddingPl(a => !a);
              }}
            >
              {addingPl && (
                <Dropdown>
                  <input
                    autoFocus
                    value={newPlName}
                    placeholder="Name"
                    onChange={e => setNewPlName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addPlaylist(newPlName)}
                  />
                  <button onClick={() => addPlaylist(newPlName)}>Add</button>
                </Dropdown>
              )}
            </ActionCard>
          </div>
        </main>

        {/* Bottom player bar */}
        <footer className="player-bar">
          <audio
            ref={el => {
              if (el) {
                el.crossOrigin = 'anonymous';
                audioRef.current = el;
              }
            }}
            preload="metadata"
          />

          {/* Left */}
          <div className="pb-left">
            <div className="pb-titles">
              <div className="pb-title" title={currentItem?.title || ''}>
                {currentItem ? truncate(currentItem.title, 40) : ''}
              </div>
              <div className="pb-sub">{currentItem?.duration || '--:--'}</div>
            </div>
          </div>

          {/* Center */}
          <div className="pb-center">
            <div className="pb-controls">
              <button
                className={`pb-icon-btn${shuffle ? ' active' : ''}`}
                onClick={() =>
                  setShuffle(s => {
                    if (!s) setRepeatOne(false);
                    return !s;
                  })
                }
                title={shuffle ? 'Shuffle' : 'In order'}
              >
                {shuffle ? '↭' : '↻'}
              </button>

              <button
                className="pb-icon-btn"
                onClick={() => {
                  const idx = visibleQueue.findIndex(i => i.id === currentId);
                  if (idx > 0) playItem(visibleQueue[idx - 1].id, true);
                }}
                disabled={!currentItem || visibleQueue.findIndex(i => i.id === currentId) <= 0}
                title="Previous"
              >
                ⏮
              </button>

              <button
                className="pb-icon-btn big"
                onClick={() => requireAuth(togglePlay)}
                disabled={!currentItem}
                title="Play/Pause"
              >
                {isPlaying ? '❚❚' : '▶'}
              </button>

              <button
                className="pb-icon-btn"
                onClick={() => {
                  if (!currentItem) return;
                  if (shuffle) {
                    const next =
                      visibleQueue[Math.floor(Math.random() * visibleQueue.length)];
                    playItem(next.id, true);
                  } else {
                    const idx = visibleQueue.findIndex(i => i.id === currentId);
                    const nextIdx = idx < 0 ? 0 : (idx + 1) % visibleQueue.length;
                    playItem(visibleQueue[nextIdx].id, true);
                  }
                }}
                disabled={!currentItem}
                title="Next"
              >
                ⏭
              </button>

              <button
                className={`pb-icon-btn${repeatOne ? ' active' : ''}`}
                onClick={() =>
                  setRepeatOne(r => {
                    if (!r) setShuffle(false);
                    return !r;
                  })
                }
                title="Repeat one"
              >
                ↺
              </button>
            </div>

            <div
              className="pb-progress-wrap"
              onClick={e => {
                if (!currentItem) return;
                const bar = e.currentTarget.querySelector('.pb-progress-bar');
                if (!bar) return;
                const rect = bar.getBoundingClientRect();
                const ratio = (e.clientX - rect.left) / rect.width;
                seekTo(Math.max(0, Math.min(1, ratio)));
              }}
            >
              <span className="pb-time">{secondsToMinStr(playSec)}</span>
              <div className="pb-progress-bar">
                <div className="pb-progress-fill" style={{ width: `${playProgress * 100}%` }} />
              </div>
              <span className="pb-time">{currentItem?.duration || '--:--'}</span>
            </div>
          </div>

          {/* Right */}
          <div className="pb-volume">
            <span className="pb-vol-icon">VOL</span>
            <input
              className="pb-vol-slider"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={e => setVolume(Number(e.target.value))}
            />
          </div>
        </footer>
      </div>
    </>
  );
}

/* ───────── UI components ───────── */
function ActionCard({ label, active, onClick, children }) {
  return (
    <button className={`lytm-action-card${active ? ' is-active' : ''}`} onClick={onClick}>
      {label}
      {children}
    </button>
  );
}

function Dropdown({ children, align = 'left' }) {
  return (
    <div className={`lytm-dropdown align-${align}`} onClick={e => e.stopPropagation()}>
      {children}
    </div>
  );
}

function PlaylistRow({
  item,
  index,
  active,
  onPlay,
  onRename,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  playlists,
  onMove,
  onCreateAndMove,
  openPlFor,
  setOpenPlFor,
}) {
  const [editing, setEditing] = useState(false);
  const [tmpTitle, setTmpTitle] = useState(item.title);
  const [newPlName, setNewPlName] = useState('');

  const showPlPick = openPlFor === item.id;

  const commit = () => {
    onRename(item.id, tmpTitle.trim() || item.title);
    setEditing(false);
  };

  return (
    <li
      className={`lytm-list-item${active ? ' is-active' : ''}`}
      onClick={() => onPlay(item.id)}
      draggable
      onDragStart={e => onDragStart(e, item.id)}
      onDragOver={onDragOver}
      onDrop={e => onDrop(e, item.id)}
      onDragEnd={onDragEnd}
    >
      <span className="lytm-list-index">{index + 1}</span>

      {editing ? (
        <input
          className="lytm-rename-input"
          value={tmpTitle}
          onChange={e => setTmpTitle(e.target.value)}
          onBlur={e => {
          e.stopPropagation();
            commit();
          }}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setTmpTitle(item.title);
              setEditing(false);
            }
          }}
          autoFocus
        />
      ) : (
        <span
          className="lytm-list-title"
          title={item.title}
          onDoubleClick={e => {
            e.stopPropagation();
            setEditing(true);
          }}
        >
          {item.title}
        </span>
      )}

      <span className="lytm-list-duration">{item.duration || '--:--'}</span>

      <div className="lytm-item-actions">
        <button
          className="icon-btn"
          onClick={e => {
            e.stopPropagation();
            setEditing(true);
          }}
        >
          ⌶
        </button>
        <button
          className="icon-btn"
          onClick={e => {
            e.stopPropagation();
            setOpenPlFor(showPlPick ? null : item.id);
          }}
        >
          ＋
        </button>
        <button
          className="icon-btn"
          onClick={e => {
            e.stopPropagation();
            onDelete(item.id);
          }}
        >
          x
        </button>
      </div>

      {showPlPick && (
        <Dropdown align="right">
          <div className="pl-picker" onClick={e => e.stopPropagation()}>
            {playlists.length > 0 &&
              playlists.map(pl => (
                <button
                  key={pl.id}
                  onClick={e => {
                    e.stopPropagation();
                    onMove(item.id, pl.id);
                    setOpenPlFor(null);
                  }}
                >
                  {pl.name}
                </button>
              ))}

            <hr className="lytm-hr" />

            <div className="lytm-newpl-box">
              <input
                placeholder="+ new playlist"
                value={newPlName}
                onChange={e => setNewPlName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newPlName.trim()) {
                    onCreateAndMove(newPlName.trim(), item.id);
                    setNewPlName('');
                    setOpenPlFor(null);
                  }
                }}
              />
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (!newPlName.trim()) return;
                  onCreateAndMove(newPlName.trim(), item.id);
                  setNewPlName('');
                  setOpenPlFor(null);
                }}
              >
                Add
              </button>
            </div>
          </div>
        </Dropdown>
      )}
    </li>
  );
}

/* ───────── utils ───────── */
async function processAudio({ url }) {
  const res = await fetch(`${API}/api/yt-media/process`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, ops: { audio: true } }),
  });

  // If the server accidentally serves HTML, fail loudly so you see it in console
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    const text = await res.text();
    throw new Error(`Expecting JSON, got: ${text.slice(0, 120)}...`);
  }

  if (!res.ok) throw new Error(await res.text());
  return res.json();
}





function truncate(str, n) {
  return str.length > n ? str.slice(0, n - 1) + '…' : str;
}
function secondsToMinStr(sec) {
  if (sec == null || isNaN(sec)) return '--:--';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}
function extractTitleGuess(url) {
  try {
    return new URL(url).searchParams.get('v') || url;
  } catch {
    return url;
  }
}
function formatMs(ms) {
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
}
function sanitizeName(str) {
  return str.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'file';
}
async function downloadBlob(url, filename) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const blob = await res.blob();
  const a = document.createElement('a');
  const obj = URL.createObjectURL(blob);
  a.href = obj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(obj);
    a.remove();
  }, 0);
}
